
import { CLog } from '../../consoleMixin'
import TagMappings from '../../../shared/TagMappings.json'

/**
 * @typedef {import('../../api-v2/js-doc').V1InvoiceUpdateResponse} V1InvoiceUpdateResponse
 * @typedef {import('../../api-v2/js-doc').V1UpdatedByUser} V1UpdatedByUser
 * @typedef {import('../../api-v2/js-doc').V2InvoiceResponse} V2InvoiceResponse
 * @typedef {import('../../api-v2/js-doc').V2InvoiceActivityLogResponse} V2InvoiceActivityLogResponse
 * @typedef {import('../../api-v2/js-doc').V2InvoiceStatus} V2InvoiceStatus
 * @typedef {import('../../api-v2/js-doc').UIInvoiceUpdates} UIInvoiceUpdates
 * @typedef {import('../../api-v2/js-doc').UIInvoiceUpdateDuplicateLink} UIInvoiceUpdateDuplicateLink
 */

const otherTagMappings = {
  'CREATE_INVOICE': 'Invoice Created',
  'DELETE_INVOICE': 'Invoice Deleted',
  'DOCUMENTS_ADD': 'Invoice Updated', // should look like in v1
  'NOTE': 'Note',
  'NOTIFICATION': 'Notification',
  'BROKER_PAYMENT_SHORTPAY': 'Payment Shortpay',
  'BROKER_PAYMENT_OVERPAY' : 'Payment Overpay',
  'BROKER_PAYMENT_NON_PAYMENT': 'Nonpayment',
  'BROKER_PAYMENT_IN_FULL' : 'Invoice Paid',
  'BROKER_PAYMENT_UPDATE': 'Payment updated',
  'UPDATE_INVOICE': 'Invoice Updated',
  'BROKER_PAYMENT_DELETE': 'Payment Removed',
  'CREATE_CLIENT_BROKER_ASSIGNMENT': 'Client Broker Assignment created',
  'UPDATE_CLIENT_BROKER_ASSIGNMENT': 'Client Broker Assignment updated',
  'BROKER_PAYMENT_SCHEDULED': 'payment',
  'BROKER_CANCELLED_LOAD': 'broker cancelled load',
  'MAIL_INVOICE_COPY': 'mail invoice copy',
  'MAIL_INVOICE_ORIGINAL': 'originals required',
  'UPLOAD_INVOICE_TO_PORTAL': 'upload to portal',
  'CLIENT_LIMIT_EXCEEDED': 'client limit exceeded',
  'BROKER_LIMIT_EXCEEDED': 'broker limit exceeded'
}

const verifyInvoiceTagMappings = {
  'in_progress': 'waiting on verification',
  'bypassed': 'Invoice Verification Skipped',
  'verified': 'Invoice verified',
}

/**
 * @param {string} invoiceUpdate - invoice update to be converted as activity log
 * @returns {string|undefined} - v2 activity log key or undefined if not found
 */
export const findActivityByInvoiceUpdate = (invoiceUpdate) => {
  for (const [key, values] of Object.entries(TagMappings)) {
    if (Array.isArray(values) && values.some(val => val === invoiceUpdate)) {
      return key;
    }
  }
  return undefined
};

/**
 * @param {V2InvoiceResponse} invoice - The invoice
 * @returns {UIInvoiceUpdates[]}
 */
export const mapActivitiesToInvoiceUpdates = (invoice) => {
  const activities = [ ...invoice.activities ]
  return activities.sort((a,b) => {
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  }).map((activity) => {
    return mapActivityToInvoiceUpdate(activity, invoice.id, invoice.status)
  })
}

/**
 * @param {V2InvoiceActivityLogResponse} activity - Invoice activity
 * @param {string} invoiceId - Invoice ID
 * @param {V2InvoiceStatus} invoiceStatus - Invoice status
 * @returns {UIInvoiceUpdates}
 */
const mapActivityToInvoiceUpdate = (activity, invoiceId, invoiceStatus) => {
  const updateStatus = getActivityUpdateStatus(activity, invoiceStatus)
  const invoiceUpdate = {
    allegedly_fixed: activity.tagStatus === 'Inactive',
    changelog_notes: '',
    check_number_or_ach: activity.payload.check_number_or_ach,
    created_at: activity.createdAt,
    firebase_key: '',
    id: activity.id,
    invoice_id: invoiceId,
    issue: activity.createdAt,
    "issue date": activity.payload['issue date'],
    metadata: activity.payload.metadata || {},
    missing: activity.payload.missing,
    notes: mapNotes(updateStatus, activity),
    update_status: updateStatus,
    update_type: mapUpdateType(activity),
    updated_at: activity.createdAt,
    updated_by_user: mapUpdatedByUser(activity),
    updated_by: activity.createdBy.id,
    user_name: mapAssignedBy(activity),
    talked_to: activity.payload.talked_to,
    talked_to_contact_method: activity.payload.talked_to_contact_method,
  }

  if (activity.tagDefinition.key === 'POSSIBLE_DUPLICATE_INVOICE') {
    const duplicateLinks = getPossibleDuplicatesLinks(activity)
    invoiceUpdate.duplicate_links = duplicateLinks
  }

  return invoiceUpdate
}

/**
 * @param {V2InvoiceActivityLogResponse} activity - Invoice activity
 * @param {V2InvoiceStatus} invoiceStatus - Invoice status
 * @returns {string}
 */
const getActivityUpdateStatus = (activity, invoiceStatus) => {
  if (invoiceStatus === 'rejected' && activity.note.startsWith('Declined because:')) {
    return 'Invoice declined'
  }

  const { level, key } = activity.tagDefinition
  switch (level) {
    case 'error':
    case 'warning':
      if (key === 'VERIFY_INVOICE') {
        const updateStatus = verifyInvoiceTagMappings[activity?.payload?.status]
        return updateStatus || ''
      }

      if (!TagMappings[key]) {
        CLog(`Missing tag mapping for ${key}, please add it to fix this.`)
        return ''
      }
      return TagMappings[key][0]
    case 'info':
    case 'other':
    default:
      if (!otherTagMappings[key]) {
        CLog(`Missing other tag mapping for ${key}, please add it to fix this.`)
        return ''
      }
      return otherTagMappings[key]
  }
}

/**
 * @param {V2InvoiceActivityLogResponse} activity - Invoice activity
 * @returns {UIInvoiceUpdateDuplicateLink[]}
 */
const getPossibleDuplicatesLinks = (activity) => {
  const duplicates = activity.payload?.data?.duplicates || []

  if (duplicates.length === 0) {
    CLog('Missing possible duplicates from activity log')
  }

  const links = duplicates.map((duplicate, index) => {
    return {
      comma: index !== duplicates.length - 1,
      invoice_id: duplicate.id,
      load_number: duplicate.loadNumber,
    }
  })
  return links
}

/**
 * @param {V2InvoiceActivityLogResponse} activity - Invoice activity
 * @returns {string}
 */
const mapAssignedBy = (activity) => {
  const createdBy = activity?.createdBy?.firstName || 'system'
  if (createdBy.toLowerCase().includes('system')) {
    return 'System'
  }
  return `${createdBy[0].toUpperCase()}${createdBy.slice(1)}`
}

/**
 * @param {string} updateStatus - V1 Invoice update status
 * @param {V2InvoiceActivityLogResponse} activity - Invoice activity
 * @returns {string}
 */
const mapNotes = (updateStatus, activity) => {
  if (Object.values(verifyInvoiceTagMappings).includes(updateStatus)) {
    // eslint-disable-next-line max-len
    return `${activity.note} (Talked to '${activity?.payload?.data?.contactPerson || '(unknown)'}' via '${activity?.payload?.data?.contactType || '(unknown)'}')`
  }
  return activity.note
}

/**
 * @param {V2InvoiceActivityLogResponse} activity - Invoice activity
 * @returns {string}
 */
const mapUpdateType = (activity) => {
  switch (activity.tagDefinition.key) {
    case 'NOTE': return 'note'
    case 'BROKER_PAYMENT_IN_FULL': return 'fully_paid'
    case 'BROKER_PAYMENT_SHORTPAY': return 'shortpay'
    case 'BROKER_PAYMENT_OVERPAY': return 'overpay'
    case 'BROKER_PAYMENT_NON_PAYMENT': return 'nonpayment'
    case 'UPDATE_INVOICE':
      if (activity.payload.status === 'purchase') {
        return 'approved'
      }
      if (activity.payload.status === 'rejected') {
        return 'declined'
      }
  }

  return 'invoice action'
}

/**
 * @param {V2InvoiceActivityLogResponse} activity - Invoice activity
 * @returns {V1UpdatedByUser}
 */
const mapUpdatedByUser = (activity) => {
  return {
    changelog_notes: '',
    client: null,
    created_at: activity.createdAt,
    display_id: null,
    email: activity.createdBy.email,
    employee: null,
    firebase_key: '',
    id: activity.createdBy.id,
    last_login: new Date(),
    metadata: {},
    updated_at: activity.createdAt,
    updated_by: activity.createdBy.id,
    user_description: null,
  }
}
